import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

interface MapLocation {
    id: string;
    type: 'registered' | 'diagnosing' | 'waitingMaterial' | 'repairing' | 'waitingClient' | 'completed';
    coordinates: string | null;
    label: string | null;
    clientName: string | null;
    projectName: string | null;
    city: string | null;
    servicingId: number | null;
    priority: number | null;
}

interface Props {
    locations: MapLocation[];
    onMarkerClick?: (item: MapLocation) => void;
}

var PIN_VISUAL: Record<string, { color: string; id: string; defaultMessage: string }> = {
    registered:      { color: '#94a3b8', id: 'service.strip.registered',    defaultMessage: 'E regjistruar' },
    diagnosing:      { color: '#3b82f6', id: 'service.strip.diagnosing',    defaultMessage: 'Në diagnostikim' },
    waitingMaterial: { color: '#f59e0b', id: 'service.map.waitingMaterial', defaultMessage: 'Në pritje të materialit' },
    repairing:       { color: '#8b5cf6', id: 'service.strip.repairing',     defaultMessage: 'Në riparim' },
    waitingClient:   { color: '#f97316', id: 'service.map.waitingClient',   defaultMessage: 'Në pritje të klientit' },
    completed:       { color: '#22c55e', id: 'service.strip.completed',     defaultMessage: 'E përfunduar' },
};

type StatusKey = keyof typeof PIN_VISUAL;

var PRIORITY_I18N: Record<number, { id: string; defaultMessage: string }> = {
    1: { id: 'service.priority.low',    defaultMessage: 'E ulët' },
    2: { id: 'service.priority.normal', defaultMessage: 'Normale' },
    3: { id: 'service.priority.high',   defaultMessage: 'E lartë' },
    4: { id: 'service.priority.urgent', defaultMessage: 'Urgjente' },
};

var createIcon = function(color: string) {
    return L.divIcon({
        html: '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">' +
            '<path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.27 21.73 0 14 0z" fill="' + color + '" stroke="#fff" stroke-width="2"/>' +
            '<circle cx="14" cy="14" r="5.5" fill="#fff"/>' +
            '</svg>',
        className: 'servicing-map-marker',
        iconSize: [28, 40],
        iconAnchor: [14, 40],
        popupAnchor: [0, -40],
    });
};

var icons: Record<string, L.DivIcon> = {};
Object.keys(PIN_VISUAL).forEach(function(key) {
    icons[key] = createIcon(PIN_VISUAL[key].color);
});

var parseCoordinates = function(coords: string | null): [number, number] | null {
    if (!coords) return null;
    var parts = coords.split(',').map(function(s) { return parseFloat(s.trim()); });
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return [parts[0], parts[1]];
    return null;
};

var ServicingMapView: React.FC<Props> = function(props) {
    var intl = useIntl();
    var fm = function(id: string, defaultMessage: string, values?: Record<string, any>) {
        return intl.formatMessage({ id: id, defaultMessage: defaultMessage }, values);
    };

    var _af = useState<Record<StatusKey, boolean>>({
        registered: true, diagnosing: true, waitingMaterial: true,
        repairing: true, waitingClient: true, completed: false,
    });
    var activeFilters = _af[0]; var setActiveFilters = _af[1];

    var toggleFilter = function(key: StatusKey) {
        setActiveFilters(function(prev) {
            var next: any = {};
            Object.keys(prev).forEach(function(k) { next[k] = (prev as any)[k]; });
            next[key] = !next[key];
            return next;
        });
    };

    var counts: Record<string, number> = {};
    Object.keys(PIN_VISUAL).forEach(function(k) { counts[k] = 0; });
    (props.locations || []).forEach(function(loc) {
        if (loc.coordinates && parseCoordinates(loc.coordinates)) {
            counts[loc.type] = (counts[loc.type] || 0) + 1;
        }
    });

    var markers: Array<{ position: [number, number]; item: MapLocation }> = [];
    (props.locations || []).forEach(function(loc) {
        if (!loc.coordinates || !activeFilters[loc.type as StatusKey]) return;
        var pos = parseCoordinates(loc.coordinates);
        if (!pos) return;
        markers.push({ position: pos, item: loc });
    });

    var defaultCenter: [number, number] = markers.length > 0 ? markers[0].position : [42.6629, 21.1655];

    return (
        <div style={{ flex: 1, minHeight: 0, borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
            <MapContainer center={defaultCenter} zoom={8} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                />
                {markers.map(function(m) {
                    return (
                        <Marker key={m.item.id} position={m.position} icon={icons[m.item.type] || icons.registered}
                            eventHandlers={{ click: function() { if (props.onMarkerClick) props.onMarkerClick(m.item); } }}>
                            <Popup>
                                <div style={{ minWidth: 180 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: PIN_VISUAL[m.item.type]?.color, flexShrink: 0 }} />
                                        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                            {PIN_VISUAL[m.item.type] && fm(PIN_VISUAL[m.item.type].id, PIN_VISUAL[m.item.type].defaultMessage)}
                                        </span>
                                    </div>
                                    {m.item.label && <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>{m.item.label}</div>}
                                    {m.item.clientName && <div style={{ fontSize: 12, color: '#475569', marginBottom: 2 }}>{m.item.clientName}</div>}
                                    {m.item.projectName && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>{m.item.projectName}</div>}
                                    {m.item.city && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>{m.item.city}</div>}
                                    {m.item.priority && m.item.priority > 1 && (
                                        <div style={{ fontSize: 10, fontWeight: 600, color: m.item.priority >= 3 ? '#ef4444' : '#64748b', marginBottom: 4 }}>
                                            {PRIORITY_I18N[m.item.priority] ? fm(PRIORITY_I18N[m.item.priority].id, PRIORITY_I18N[m.item.priority].defaultMessage) : ''}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                                        {m.item.servicingId && (
                                            <a href={'/dashboard/service/' + m.item.servicingId}
                                                style={{ fontSize: 11, color: '#2a5a8a', textDecoration: 'none', fontWeight: 600 }}>
                                                {fm('service.map.viewServicing', 'Shiko servisimin')}
                                            </a>
                                        )}
                                        <a href={'https://www.google.com/maps/dir/?api=1&destination=' + m.position[0] + ',' + m.position[1]}
                                            target="_blank" rel="noopener noreferrer"
                                            style={{ fontSize: 11, color: '#64748b', textDecoration: 'none', marginLeft: 'auto' }}>
                                            {fm('service.map.googleMaps', 'Google Maps →')}
                                        </a>
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>

            {/* Filter toggles */}
            <div style={{
                position: 'absolute', top: 12, right: 12, zIndex: 1000,
                background: '#fff', borderRadius: 10, padding: 6,
                boxShadow: '0 2px 16px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
                display: 'flex', flexDirection: 'column', gap: 2, minWidth: 180,
            }}>
                {(Object.keys(PIN_VISUAL) as StatusKey[]).map(function(key) {
                    var cfg = PIN_VISUAL[key];
                    var active = activeFilters[key];
                    var count = counts[key] || 0;
                    return (
                        <button key={key} onClick={function() { toggleFilter(key); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '7px 12px', border: 'none', borderRadius: 6,
                                background: active ? '#f8fafc' : 'transparent',
                                cursor: 'pointer', transition: 'all 0.15s',
                                opacity: active ? 1 : 0.35,
                                width: '100%', textAlign: 'left',
                            }}>
                            <div style={{
                                width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                                background: cfg.color,
                                boxShadow: active ? '0 0 0 2px #fff, 0 0 0 3.5px ' + cfg.color : 'none',
                                transition: 'box-shadow 0.15s',
                            }} />
                            <span style={{ fontSize: 12, color: '#0f172a', fontWeight: 500, flex: 1 }}>{fm(cfg.id, cfg.defaultMessage)}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, minWidth: 24, textAlign: 'right', color: active ? '#0f172a' : '#94a3b8' }}>{count}</span>
                        </button>
                    );
                })}
                <div style={{ borderTop: '1px solid #e2e8f0', margin: '4px 0 2px', padding: '6px 12px 2px' }}>
                    <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{fm('service.map.pointsInMap', '{count} pika në hartë', { count: markers.length })}</span>
                </div>
            </div>
        </div>
    );
};

export default ServicingMapView;
